import { ChatRequestError, chatError } from './client'
import type { ChatRecovery } from './recovery'
import type { ChatAttempt, ChatMessageInput } from './types'

type SendState = {
  ready: boolean
  busy: boolean
  pending: ChatAttempt | null
  rejected: boolean
  error: string | null
}

type Dependencies = {
  scope: string
  recovery: ChatRecovery
  lock: <T>(action: () => Promise<T>) => Promise<T>
  submit: (attempt: ChatAttempt, captcha?: string) => Promise<number>
}

export class ChatSendController {
  private state: SendState = {
    ready: false,
    busy: false,
    pending: null,
    rejected: false,
    error: null,
  }
  private listeners = new Set<() => void>()

  constructor(private readonly dependencies: Dependencies) {}

  snapshot = () => this.state

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private update(values: Partial<SendState>) {
    this.state = { ...this.state, ...values }
    this.listeners.forEach(listener => listener())
  }

  async restore() {
    if (this.state.busy) return
    try {
      await this.dependencies.lock(async () => {
        const pending = await this.dependencies.recovery.read(
          this.dependencies.scope
        )
        this.update({
          ready: true,
          pending,
          rejected: pending?.state === 'Rejected',
        })
      })
    } catch (error) {
      this.update({ ready: false, error: chatError(error) })
    }
  }

  async send(
    action: ChatAttempt['action'],
    input: ChatMessageInput,
    browserContext: string,
    captcha?: string
  ): Promise<number | null> {
    return this.run(async () => {
      const previous = await this.dependencies.recovery.read(
        this.dependencies.scope
      )
      if (previous) {
        this.update({ pending: previous })
        throw new Error(
          'A saved submission needs to be checked before sending another message.'
        )
      }
      const attempt: ChatAttempt = {
        version: 1,
        action,
        input: structuredClone(input),
        browserContext,
        createdAt: new Date().toISOString(),
        state: 'Prepared',
        uncertainHistory: false,
      }
      await this.dependencies.recovery.write(this.dependencies.scope, attempt)
      this.update({ pending: attempt })
      return this.submit(attempt, browserContext, captcha)
    })
  }

  async retry(
    browserContext: string,
    captcha?: string
  ): Promise<number | null> {
    return this.run(async () => {
      const attempt = await this.dependencies.recovery.read(
        this.dependencies.scope
      )
      if (!attempt) {
        this.update({ pending: null })
        return null
      }
      this.update({ pending: attempt })
      return this.submit(attempt, browserContext, captcha)
    })
  }

  private async submit(
    attempt: ChatAttempt,
    browserContext: string,
    captcha?: string
  ) {
    if (attempt.browserContext !== browserContext) {
      throw new Error(
        'This saved message belongs to an earlier browser conversation. It will not be sent into this one.'
      )
    }
    attempt = {
      ...attempt,
      state: 'Submitting',
      uncertainHistory:
        attempt.uncertainHistory || attempt.state === 'Submitting',
    }
    await this.dependencies.recovery.write(this.dependencies.scope, attempt)
    this.update({ pending: attempt, rejected: false })
    let id: number
    try {
      id = await this.dependencies.submit(attempt, captcha)
      if (!Number.isInteger(id) || id <= 0)
        throw new ChatRequestError(
          'The message could not be confirmed. Retry the same submission.'
        )
    } catch (error) {
      if (
        !attempt.uncertainHistory &&
        error instanceof ChatRequestError &&
        [400, 413, 415].includes(error.status)
      ) {
        // Keep the original bytes until the visitor explicitly restores them for
        // editing. A rejection after refresh must not lose text or attachments.
        const rejected: ChatAttempt = { ...attempt, state: 'Rejected' }
        await this.dependencies.recovery.write(
          this.dependencies.scope,
          rejected
        )
        this.update({ pending: rejected, rejected: true })
      }
      throw error
    }
    await this.dependencies.recovery.clear(this.dependencies.scope)
    this.update({ pending: null, rejected: false })
    return id
  }

  async forget() {
    const expected = this.state.pending
    await this.run(async () => {
      const stored = await this.dependencies.recovery.read(
        this.dependencies.scope
      )
      if (
        stored &&
        (stored.input.requestId !== expected?.input.requestId ||
          stored.browserContext !== expected?.browserContext)
      ) {
        throw new Error(
          'The saved submission changed in another tab. Check it again before forgetting this copy.'
        )
      }
      await this.dependencies.recovery.clear(this.dependencies.scope)
      this.update({ pending: null, rejected: false })
      return null
    })
  }

  async editRejected(): Promise<ChatAttempt | null> {
    if (!this.state.rejected) return null
    return this.run(async () => {
      const attempt = await this.dependencies.recovery.read(
        this.dependencies.scope
      )
      if (
        attempt?.input.requestId !== this.state.pending?.input.requestId ||
        attempt?.state !== 'Rejected'
      ) {
        throw new Error(
          'The saved submission changed in another tab. Check it again before editing.'
        )
      }
      await this.dependencies.recovery.clear(this.dependencies.scope)
      this.update({ pending: null, rejected: false })
      return attempt
    })
  }

  private async run<T>(action: () => Promise<T>): Promise<T | null> {
    if (this.state.busy || !this.state.ready) return null
    this.update({ busy: true, error: null })
    try {
      return await this.dependencies.lock(action)
    } catch (error) {
      this.update({ error: chatError(error) })
      return null
    } finally {
      this.update({ busy: false })
    }
  }
}
